<script lang="ts" setup>
import type { ExchangeGroupingFilterEnumType } from '@forgd/shared/enums'
import type { FunctionalComponent } from 'vue'
import { formatSignedPercent } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@forgd/ui-kit/table'
import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

type Trend = 'up' | 'down' | 'flat'

const FEATURED_EXCHANGE_COUNT = 2

const TREND_STYLE: Record<Trend, { color: string, icon?: FunctionalComponent }> = {
  up: { color: 'text-success', icon: ArrowUpRightIcon },
  down: { color: 'text-error', icon: ArrowDownRightIcon },
  flat: { color: 'text-primal' },
}

const listingsQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/listings-by-exchange', {
    params: () => ({ query: { exchangeGroup: 'tier1' as const } }),
  }),
  refetchOnWindowFocus: false,
})

const performanceQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/primary-listing-performance', {
    params: () => ({ query: { exchangeGroup: 'individual' as ExchangeGroupingFilterEnumType } }),
  }),
  refetchOnWindowFocus: false,
})

const isLoading = computed(() => listingsQuery.isLoading.value || performanceQuery.isLoading.value)

const metrics = computed(() => {
  const byMonth = listingsQuery.data.value?.byMonth ?? []
  const sorted = [...byMonth].sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''))
  const last = sorted.at(-1)
  const prior = sorted.at(-2)

  function buildMetric(label: string, currCount: number | undefined, priorCount: number | undefined) {
    if (!priorCount || currCount === undefined) {
      return { label, value: null, trend: 'flat' as Trend }
    }
    const pct = ((currCount - priorCount) / priorCount) * 100
    const trend: Trend = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
    return { label, value: formatSignedPercent(pct, 2), trend }
  }

  return [
    buildMetric('MoM Primary Listings', last?.primaryCount, prior?.primaryCount),
    buildMetric('MoM Follow-On Listings', last?.followOnCount, prior?.followOnCount),
  ]
})

const exchanges = computed(() => {
  const byExchange = listingsQuery.data.value?.byExchange ?? []
  const priceSeries = performanceQuery.data.value?.avgPriceChangeByExchange ?? []
  const fdvSeries = performanceQuery.data.value?.avgFdvByExchange ?? []

  const rows = byExchange.slice(0, FEATURED_EXCHANGE_COUNT).map((meta) => {
    const id = meta.exchangeName
    const price = priceSeries.find(p => p.exchangeName === id && p.timeframe === '1w')
    const fdv = fdvSeries.find(p => p.exchangeName === id && p.timeframe === '1w')
    return {
      name: id,
      logoSrc: meta.logo ?? null,
      weekOneRoi: price ? formatSignedPercent(price.value, 1) : null,
      avgFdv: fdv ? formatValue(fdv.value, 'currency:short,2') : null,
    }
  })

  while (rows.length < FEATURED_EXCHANGE_COUNT) {
    rows.push({ name: '', logoSrc: null, weekOneRoi: null, avgFdv: null })
  }
  return rows
})
</script>

<template>
  <Card class="flex h-full flex-col">
    <CardHeader class="flex flex-row items-center justify-between border-b border-default p-3 sm:pl-5">
      <div class="flex items-center gap-2">
        <CardTitle>
          Exchange Listings
        </CardTitle>
        <Badge variant="gray">
          30D
        </Badge>
      </div>
      <Button
        as-child
        variant="outline"
        size="sm"
        icon-only
      >
        <NuxtLink to="/research/exchange-and-listing" aria-label="Open Exchange Listings">
          <ArrowRightIcon />
        </NuxtLink>
      </Button>
    </CardHeader>

    <div class="flex flex-1 flex-col gap-4 p-3 sm:p-4">
      <div class="grid grid-cols-2 gap-4">
        <div v-for="m in metrics" :key="m.label" class="flex flex-col gap-1">
          <span class="text-2xs text-secondary">{{ m.label }}</span>
          <Skeleton v-if="isLoading" class="h-6 w-20" />
          <span
            v-else
            class="flex items-center gap-1 text-base font-semibold"
            :class="TREND_STYLE[m.trend].color"
          >
            {{ m.value ?? 'N/A' }}
            <component
              :is="TREND_STYLE[m.trend].icon"
              v-if="TREND_STYLE[m.trend].icon"
              class="size-4"
            />
          </span>
        </div>
      </div>

      <div class="mt-auto overflow-hidden rounded-md border border-default">
        <Table class="table-fixed">
          <TableBody class="divide-y divide-default">
            <TableRow class="h-8 text-2xs font-normal text-secondary">
              <TableHead class="px-3 py-1 text-2xs">
                Exchange
              </TableHead>
              <TableHead class="w-24 px-3 py-1 text-right text-2xs">
                Avg. Week-1 ROI
              </TableHead>
              <TableHead class="w-20 px-3 py-1 text-right text-2xs">
                Avg. FDV
              </TableHead>
            </TableRow>
            <TableRow
              v-for="(ex, i) in exchanges"
              :key="ex.name || `placeholder-${i}`"
              class="h-8"
            >
              <TableCell class="px-3 py-1">
                <div class="flex min-w-0 items-center gap-2">
                  <template v-if="isLoading">
                    <Skeleton class="size-5 shrink-0 rounded-full" />
                    <Skeleton class="h-4 w-20" />
                  </template>
                  <template v-else>
                    <Avatar class="size-5 shrink-0">
                      <AvatarImage v-if="ex.logoSrc" :src="ex.logoSrc" :alt="ex.name" />
                      <AvatarFallback class="text-2xs">
                        {{ ex.name.slice(0, 1) }}
                      </AvatarFallback>
                    </Avatar>
                    <span class="truncate text-xs text-secondary capitalize">{{ ex.name }}</span>
                  </template>
                </div>
              </TableCell>
              <TableCell class="px-3 py-1">
                <Skeleton v-if="isLoading" class="ml-auto h-4 w-14" />
                <span v-else class="font-mono text-xs font-medium">{{ ex.weekOneRoi ?? 'N/A' }}</span>
              </TableCell>
              <TableCell class="px-3 py-1">
                <Skeleton v-if="isLoading" class="ml-auto h-4 w-14" />
                <span v-else class="font-mono text-xs font-medium">{{ ex.avgFdv ?? 'N/A' }}</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  </Card>
</template>
